import Queue from '../Queue';
import './Dashboard.scss';
import { prefixClass } from 'lib/utils';
import { useIsMobile } from 'hooks/useIsMobile';
import DashboardCardList from './DashboardListGroup/DashboardCardList';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import KSChart from '../KSChart/KSChart';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { shallow } from 'zustand/shallow';
import { useTemplatesRangesQuery } from 'hooks/reports/useTemplatesRangesQuery';
import reportToDashboard from './DParser/reportToDashboard';
import { useDashboardReport } from './useDashboardReport';
import classNames from 'classnames';
import { LocalErrorBoundary } from 'components/ErrorBoundary';

// TO-DO replace this import with the one on hone-automation/common
import { MetricsPlot } from './DParser/types';
import { logger } from '../KSExport/reportNTParser/utils';
import { useGetReportConfiguration } from '../../../hooks';
import { getPeriodsWeeksChartData } from './DParser/DPeriodStructure';
import { useDashboardChartDataQuery } from 'hooks/useDashboardChartDataQuery';
import {
  IconChevron,
  IconComment,
  IconMention,
  IconFilter,
  IconNavQueue,
  IconSort,
  IconForward,
  IconChevronDown,
  IconLoupe,
  FilterActual,
} from 'components/Icons';
import { Tooltip, Whisper } from 'rsuite';
import DashboardEmail from './DashboardEmail/DashboardEmail';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FilterPanel } from '../KSQ2/Panels/FilterPanel';
import { BrowserPanel } from '../KSQ2/Panels/BrowserPanel';
import { ConversationPanel } from '../KSQ2/Panels/ConversationPanel';
import { EmptyState } from '../KSQ2/Common/EmptyState';
import { MobileKSQ2Layout } from '../KSQ2/Mobile';
import { QueuePanelProvider, QueuePanelSystem } from '../Queue/common/QueuePanel';
import { useQueueContext } from '../Queue/QueueContext';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  BatchQueueProvider,
  useBatchQueueContext,
  BatchOperationType,
  BatchOperationValue,
  StatusValue,
} from '../KSQ2/contexts';
import { getDropOperation, executeDropOperationBatched } from '../KSQ2/Panels/FilterPanel/utils';
import { useBulkAddToGroup } from 'hooks/queue';
import type { AskRequest, QueueView } from '@hone-automation/common';
import { usePatchQueueRequestMutation } from 'hooks/queue';
import { RequestCard } from '../KSQ2/Panels/BrowserPanel/components/RequestCard';
import { formatRangeValues } from 'lib/reportUtils';

/**
 * Tips to display in the ConversationPanel empty state
 * These help users discover features while they wait
 */
const QUEUE_TIPS = [
  {
    icon: <IconLoupe />,
    title: 'Search by ID',
    description: 'Use #1234 in the search bar to quickly find a request by its ID',
  },
  {
    icon: <IconMention />,
    title: 'Filter by mentions',
    description: 'Click the @ button to see requests where you were mentioned',
  },
  {
    icon: <IconFilter />,
    title: 'Advanced filters',
    description: 'Use the filter button to narrow down by status, due date, or assignee',
  },
  {
    icon: <IconNavQueue />,
    title: 'Drag and drop',
    description: 'Drag requests to different filters to quickly change their status',
  },
  {
    icon: <IconSort />,
    title: 'Sort your way',
    description: 'Change the date field to sort by last message, due date, or creation date',
  },
  {
    icon: <IconComment />,
    title: 'Multi-select',
    description: 'Use Cmd/Ctrl+Click or Shift+Click to select multiple requests at once',
  },
  {
    icon: <FilterActual />,
    title: 'Search by amount',
    description: 'Type a number like 100, or use >500, <200, or 50-100 to filter by amount',
  },
  {
    icon: <IconChevronDown />,
    title: 'Collapse all groups',
    description: 'Double-click any date header to collapse or expand all date groups at once',
  },
];

const Dashboard = () => {
  const prefix = prefixClass('dashboard');
  const { queueShowEmailConfigured, enableQueueV2 } = useFlags();
  const isMobile = useIsMobile();
  // Feature flag for the new KSQ2 layout (FilterPanel with chart + BrowserPanel + ConversationPanel)
  // State allows toggling between layouts during development
  const [useKSQ2Layout, setUseKSQ2Layout] = useState(enableQueueV2);
  const trace = false;
  const [menu, setMenu] = useState<'Opportunities' | 'Requests' | 'Mentions'>('Requests');
  const [readReport, setReadReport] = useState(false);
  const [chartData, setChartData] = useState<MetricsPlot>({ periodReporting: true, periods: [], metrics: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [leftPane, setLeftPane] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Handle window resize and collapse left pane on smaller screens
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);

      // Auto-collapse left pane when window width is below 1160px
      if (newWidth < 1160 && leftPane) {
        setLeftPane(false);
      }
    };

    window.addEventListener('resize', handleResize);
    // Initial check
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [leftPane]);

  // Reading chart data from BE
  const { data: chartBEData } = useDashboardChartDataQuery();

  /*

      *** Reading current location report from store
      It's used if the BE is returning a chart that does not have ChartMetrics in the template
      To be removed once BE and Opportunities (LLM proccesing) is Ready
  */
  const { currentLocationId, currentLocation } = useLocationsStore(
    state => ({ currentLocationId: state.currentLocationId, currentLocation: state.currentLocation }),
    shallow
  );

  const { data: reportConfiguration } = useGetReportConfiguration(currentLocationId);
  const { data: availableTemplates } = useTemplatesRangesQuery();
  const templateId = availableTemplates?.[0]?.id || '';

  // @ts-ignore
  const periodStructure = getPeriodsWeeksChartData(currentLocation?.weekStart, reportConfiguration?.data, 6);
  const COMPARISON_PERIODS = periodStructure.completeWeeks;
  const { reportData } = useDashboardReport(
    currentLocation!,
    templateId,
    availableTemplates!,
    COMPARISON_PERIODS,
    readReport
  );
  /*
      *** All above is needed to read the report and compile it

  */

  useEffect(() => {
    if (chartBEData !== undefined) {
      setMenu('Requests');
      setIsLoading(false);
      if (!chartBEData?.metrics?.length) {
        setReadReport(true);
      } else {
        if (trace) logger.msg('chartData from BE ->', chartBEData);
        // @ts-ignore
        setChartData(chartBEData);
        setReadReport(false);
      }
    }
  }, [chartBEData]);

  useEffect(() => {
    if (!chartBEData || !reportData) return;
    if (trace) {
      logger.msg('reportData extracted from Report', reportData);
      logger.msg('reportConfiguration', reportConfiguration);
      logger.msg('periodStructure', periodStructure);
    }
    const { chartData: newChartData } = reportToDashboard(reportData, periodStructure, trace);
    if (trace) logger.msg('newChartData', newChartData);
    if (readReport) setChartData(newChartData);
  }, [readReport, chartBEData, reportData]);

  // Mobile KSQ2 Layout: Full mobile experience with tabs
  if (isMobile && useKSQ2Layout) {
    return (
      <BatchQueueProvider>
        <MobileKSQ2Layout chart={<KSChart data={chartData} customHeight={200} />} isLoading={isLoading} />
      </BatchQueueProvider>
    );
  }

  // Desktop KSQ2 Layout: FilterPanel (with chart) + BrowserPanel + ConversationPanel
  if (useKSQ2Layout) {
    return (
      <BatchQueueProvider>
        <DashboardKSQ2Layout chartData={chartData} isLoading={isLoading} />
      </BatchQueueProvider>
    );
  }

  // Legacy Layout: Left pane (chart + cards) + Right pane (Queue)
  return (
    <>
      <div className={prefix()}>
        <div className={classNames(prefix('left-pane'), { 'left-pane-hidden': !leftPane })}>
          <div className={prefix('left-pane__chart-container')}>
            <KSChart data={chartData} customHeight={320} />
          </div>
          <div className={prefix('left-pane__cards-container')}>
            <DashboardCardList
              onChange={(selectedCard: 'Opportunities' | 'Requests' | 'Mentions') => setMenu(selectedCard)}
              selected={menu}
            />
          </div>
          {queueShowEmailConfigured && (
            <div className={prefix('left-pane__email-container')}>
              <DashboardEmail email={currentLocation?.emailId} />
            </div>
          )}
        </div>
        <div className={classNames(prefix('right-pane'), { 'left-pane-hidden': !leftPane })}>
          <Whisper
            placement="right"
            delayClose={100}
            delayOpen={800}
            trigger="hover"
            speaker={<Tooltip>{leftPane ? 'Hide Left Pane' : 'Show Left Pane'}</Tooltip>}
          >
            <div
              className={classNames('chart-switch', { 'pane-collapsed': !leftPane })}
              onClick={() => setLeftPane(!leftPane)}
            >
              <IconChevron />
            </div>
          </Whisper>
          <div className={prefix('right-pane__view-area')}>
            <div className={classNames(prefix('right-pane__view-area-slider'), { 'slider-move': menu === 'Requests' })}>
              {!isLoading && (
                <>
                  <div className={prefix('right-pane__view-area-slider-component')}></div>
                  <div className={prefix('right-pane__view-area-slider-component')}>
                    <Queue
                      visible={menu === 'Requests'}
                      fullScreen={!leftPane}
                      leftPaneVisible={leftPane}
                      onToggleLeftPane={setLeftPane}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * KSQ2 Layout component - New dashboard layout with FilterPanel, BrowserPanel, and ConversationPanel
 */
const DashboardKSQ2Layout = ({ chartData, isLoading }: { chartData: MetricsPlot; isLoading: boolean }) => {
  const prefix = prefixClass('dashboard');
  const { state, dispatch, queryData } = useQueueContext();
  const { selectedItem, activeView, meId, visibleRequestIds } = state;
  const { queryRequests } = queryData;
  const { addBatch } = useBatchQueueContext();
  const currentLocationId = useLocationsStore(state => state.currentLocationId);
  const { addMultipleToGroup } = useBulkAddToGroup(currentLocationId || '');

  // Track previous selected item data to handle race conditions during mutations.
  // Store the FULL request object, not just ID, because during query invalidation
  // the request may temporarily be missing from the requests array.
  const prevSelectedItemRef = useRef<typeof selectedItem>(selectedItem);
  useEffect(() => {
    // Only update the ref when we have a valid selectedItem
    // This preserves the previous selection during brief null states
    if (selectedItem) {
      prevSelectedItemRef.current = selectedItem;
    }
  }, [selectedItem]);

  // For blockers view, use the effective selected item.
  // During mutations, selectedItem may briefly become null or wrong due to CMComposer's
  // handleRequestSubmit logic which sets selectedItem based on URL status param.
  // In blockers view, we fall back to the previous selection to avoid flashing empty state.
  const effectiveSelectedItem = useMemo(() => {
    // If we have a valid selectedItem, try to get the latest version from queryRequests
    if (selectedItem) return queryRequests.find(r => r.id === selectedItem.id) || selectedItem;

    // Fallback for blockers view: use the cached previous selection
    // This handles the race condition where selectedItem briefly becomes null
    if (activeView === 'Blockers' && prevSelectedItemRef.current) {
      // Try to get the latest version from queryRequests (in case it was updated)
      const latestVersion = queryRequests.find(r => r.id === prevSelectedItemRef.current?.id);
      // Return the latest version if found, otherwise use the cached version
      // (the cached version handles the case where the request is temporarily missing)
      return latestVersion || prevSelectedItemRef.current;
    }

    return null;
  }, [selectedItem, activeView, queryRequests]);

  // Check if selectedItem is visible in filtered results, or is a child of a visible group
  const isSelectedItemVisible = useMemo(() => {
    if (!effectiveSelectedItem) return false;

    // Primary check: is it in the visible request IDs?
    if (visibleRequestIds.has(effectiveSelectedItem.id)) return true;

    // Child requests inside groups aren't in visibleRequestIds (only the parent group is)
    if (effectiveSelectedItem.parentId && visibleRequestIds.has(effectiveSelectedItem.parentId)) {
      return true;
    }

    // Groups selected from FilterPanel: the group itself isn't in visibleRequestIds
    // (only its children are), but it should still be shown in ConversationPanel
    if (activeView === 'Groups' && effectiveSelectedItem.type === 'Group') {
      return true;
    }

    // Fallback for blockers view: avoid flashing empty state during mutations
    if (activeView === 'Blockers') {
      return true;
    }

    return false;
  }, [effectiveSelectedItem, visibleRequestIds, activeView]);

  // Tips carousel state - start with random tip
  const [tipIndex, setTipIndex] = useState(() => Math.floor(Math.random() * QUEUE_TIPS.length));
  const currentTip = QUEUE_TIPS[tipIndex];
  const nextTip = useCallback(() => {
    setTipIndex(prev => (prev + 1) % QUEUE_TIPS.length);
  }, []);

  // Mutation for updating request status/priority
  const patchRequestMutation = usePatchQueueRequestMutation();

  // Configure drag sensors with activation constraints
  // This allows clicks to work - dragging only starts after moving 8px
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8, // 8px movement required before drag starts
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 200, // 200ms hold before drag starts on touch
      tolerance: 5,
    },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  // Filter panel collapse state
  const [filterPanelVisible, setFilterPanelVisible] = useState(true);

  // Drag and drop state
  const [activeDragData, setActiveDragData] = useState<{ selectedIds: string[] } | null>(null);

  const overlayRequest = useMemo(() => {
    const firstId = activeDragData?.selectedIds?.[0];
    if (!firstId) return null;
    return queryRequests.find(r => r.id === firstId) ?? null;
  }, [activeDragData, queryRequests]);

  const overlayTitle = overlayRequest?.identifier || (overlayRequest ? `Request ${overlayRequest.id}` : '');
  const overlayDate = useMemo(() => {
    if (!overlayRequest) return '';
    const dateValue = overlayRequest.updatedAt || overlayRequest.createdAt;
    if (!dateValue) return '';
    const date = new Date(dateValue);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }, [overlayRequest]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current) {
      setActiveDragData(active.data.current as { selectedIds: string[] });
    }
  }, []);

  /**
   * Handle the actual API call for a single request
   * This will be called by BatchQueueContext for each item in the batch
   */
  const handleProcessItem = useCallback(
    async (requestId: string, operation: BatchOperationType, value: BatchOperationValue) => {
      const request = queryRequests.find(r => r.id === requestId);
      if (!request) {
        console.warn(`Request ${requestId} not found in queryRequests`);
        return;
      }

      // Build mutation payload based on operation type
      const payload: {
        id: string;
        locationId?: string;
        status?: StatusValue;
        priority?: 'Blocker' | 'Normal';
        assignee?: string | null;
        parentId?: string | null;
      } = {
        id: requestId,
        locationId: currentLocationId,
      };

      if (operation === 'setStatus') {
        payload.status = value as StatusValue;
      } else if (operation === 'setPriority') {
        payload.priority = value as 'Blocker' | 'Normal';
      } else if (operation === 'setAssignee') {
        payload.assignee = value === 'me' ? meId : (value as string | null);
      } else if (operation === 'setParentId') {
        payload.parentId = value as string | null;
      }

      await patchRequestMutation.mutateAsync(payload);

      // Update local state - convert null assignee to undefined for type compatibility
      const updatedRequest = { ...request, ...payload };
      if (updatedRequest.assignee === null) {
        updatedRequest.assignee = undefined;
      }
      dispatch({
        type: 'UPDATE_REQUEST',
        payload: {
          request: updatedRequest as AskRequest,
          status: payload.status || request.status,
        },
      });
    },
    [queryRequests, patchRequestMutation, dispatch, meId, currentLocationId]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveDragData(null);

      if (!over || !active.data.current) return;

      const { selectedIds } = active.data.current as { selectedIds: string[] };
      const targetId = over.id as string;

      // Check if target is a group (not a standard filter)
      const standardFilters: QueueView[] = ['Me', 'My Team', 'Client', 'Completed', 'Blockers', 'Groups'];
      const isGroupId = !standardFilters.includes(targetId as QueueView);

      // Handle group drops using local operations (respects feature flag)
      if (isGroupId) {
        addMultipleToGroup(selectedIds, targetId);
        return;
      }

      // Standard filter drops - use batch operation system
      const targetFilter = targetId as QueueView;
      const dropOp = getDropOperation(activeView, targetFilter);
      if (!dropOp.allowed) return;

      executeDropOperationBatched(selectedIds, dropOp, meId, targetFilter, addBatch, handleProcessItem);

      dispatch({ type: 'HIGHLIGHT_FILTER', payload: targetFilter });
      setTimeout(() => {
        dispatch({ type: 'HIGHLIGHT_FILTER', payload: null });
      }, 800);
    },
    [activeView, addBatch, addMultipleToGroup, handleProcessItem, meId, dispatch]
  );

  // Browser panel resize state
  const [browserWidth, setBrowserWidth] = useState(330);
  const [isDragging, setIsDragging] = useState(false);
  const dividerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(330);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      startXRef.current = e.clientX;
      startWidthRef.current = browserWidth;
      setIsDragging(true);
    },
    [browserWidth]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = e.clientX - startXRef.current;
      const newWidth = startWidthRef.current + deltaX;

      // Auto-collapse filter panel if dragging below minimum threshold
      if (filterPanelVisible && newWidth < 320) {
        setFilterPanelVisible(false);
        setIsDragging(false);
        return;
      }

      // Auto-expand filter panel if dragging above maximum threshold and panel is collapsed
      if (!filterPanelVisible && newWidth > 500) {
        setFilterPanelVisible(true);
        setIsDragging(false);
        return;
      }

      // Apply min/max constraints (320px min, 500px max)
      const constrainedWidth = Math.max(360, Math.min(newWidth, 500));
      setBrowserWidth(constrainedWidth);
    },
    [isDragging, filterPanelVisible]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Must be before any early returns to maintain consistent hook order
  const defaultChartLabel = useMemo(() => {
    const firstPeriod = chartData.periods?.[0];
    if (!firstPeriod) return undefined;

    if (!chartData.periodReporting) {
      const start = firstPeriod.date?.start;
      const end = firstPeriod.date?.end;
      return formatRangeValues(start && end ? `${start} - ${end}` : start, 'KS') || undefined;
    }

    const year = new Date().getFullYear();
    return firstPeriod.periodTitle.indexOf(year + '') > -1
      ? firstPeriod.periodTitle
      : firstPeriod.periodTitle + ` ${year}`;
  }, [chartData.periodReporting, chartData.periods]);

  if (isLoading) {
    return <div className={prefix('loading')}>Loading...</div>;
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <QueuePanelProvider>
        <div className={classNames(prefix(), prefix('ksq2'))}>
          <div className={classNames(prefix('filter-panel'), { 'filter-panel-hidden': !filterPanelVisible })}>
            <LocalErrorBoundary>
              <FilterPanel chart={<KSChart data={chartData} customHeight={320} />} chartLabel={defaultChartLabel} />
            </LocalErrorBoundary>
          </div>
          <div className={prefix('browser-panel')} style={{ width: `${browserWidth}px` }}>
            <Whisper
              placement="right"
              delayClose={100}
              delayOpen={800}
              trigger="hover"
              speaker={<Tooltip>{filterPanelVisible ? 'Hide Filter Panel' : 'Show Filter Panel'}</Tooltip>}
            >
              <div
                className={classNames('chart-switch', { 'pane-collapsed': !filterPanelVisible })}
                onClick={() => setFilterPanelVisible(!filterPanelVisible)}
              >
                <IconChevron />
              </div>
            </Whisper>
            <LocalErrorBoundary>
              <BrowserPanel showBlockersInLists={true} />
            </LocalErrorBoundary>
          </div>
          <div className={prefix('divider')} ref={dividerRef} onMouseDown={handleMouseDown} />
          <div className={prefix('conversation-panel')}>
            <LocalErrorBoundary>
              {effectiveSelectedItem && isSelectedItemVisible ? (
                // TODO: Re-enable group placeholder if needed in the future
                // effectiveSelectedItem.type === 'Group' ? (
                //   <EmptyState
                //     icon={<IconNavQueue />}
                //     title="Group selected"
                //     description="Group details panel coming soon"
                //     disabled
                //   />
                // ) : (
                <ConversationPanel request={effectiveSelectedItem} comments={effectiveSelectedItem.comments} />
              ) : (
                // )
                <EmptyState
                  icon={currentTip.icon}
                  title={currentTip.title}
                  description={currentTip.description}
                  action={{ label: 'Next tip', onClick: nextTip, icon: <IconForward /> }}
                  disabled
                />
              )}
            </LocalErrorBoundary>
          </div>
          <QueuePanelSystem />
        </div>
      </QueuePanelProvider>
      {/* Drag overlay shows what's being dragged */}
      <DragOverlay>
        {activeDragData && (
          <div
            className={classNames('ksq2-drag-overlay', {
              'ksq2-drag-overlay--multi': activeDragData.selectedIds.length > 1,
            })}
          >
            {activeDragData.selectedIds.length > 1 && (
              <>
                <div className="ksq2-drag-overlay__backdrop ksq2-drag-overlay__backdrop--2" />
                <div className="ksq2-drag-overlay__backdrop ksq2-drag-overlay__backdrop--1" />
              </>
            )}

            <div className="ksq2-drag-overlay__content">
              {overlayRequest ? (
                <RequestCard id={overlayRequest.id} title={overlayTitle} date={overlayDate} />
              ) : (
                <div className="ksq2-drag-overlay__fallback">
                  {activeDragData.selectedIds.length} item{activeDragData.selectedIds.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            {activeDragData.selectedIds.length > 1 && (
              <div className="ksq2-drag-overlay__count">{activeDragData.selectedIds.length}</div>
            )}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

const DashboardWrapped = () => (
  <LocalErrorBoundary>
    <Dashboard />
  </LocalErrorBoundary>
);

export default DashboardWrapped;
